// File: frontend/src/pages/ClassesPage.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import EmptyState from '../components/EmptyState';
import Spinner from '../components/Spinner';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { classesAPI, usersAPI, roomsAPI, studentsAPI } from '../services/api';
import Modal from '../components/Modal';

const WEEKDAY_ORDER = { Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6 };

export default function ClassesPage() {
    const { currentUser } = useAuth();
    const [classes, setClasses] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [rooms, setRooms] = useState([]);
    const [cramSchoolOptions, setCramSchoolOptions] = useState([]);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingClass, setEditingClass] = useState(null);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addError, setAddError] = useState('');
    const [newName, setNewName] = useState('');
    const [newStartTime, setNewStartTime] = useState('');
    const [newEndTime, setNewEndTime] = useState('');
    const [newDay, setNewDay] = useState('Monday');
    const [newTeacherId, setNewTeacherId] = useState('');
    const [newRoomId, setNewRoomId] = useState('');
    const [newClassType, setNewClassType] = useState('private');
    const [newFormat, setNewFormat] = useState('in_person');
    const [newCramSchoolName, setNewCramSchoolName] = useState('');
    const [newHourlyWage, setNewHourlyWage] = useState('');

    const [filters, setFilters] = useState({ day: '', teacherId: '' });
    const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'private', 'cram_school'
    const [sortBy, setSortBy] = useState('name_asc'); // 'name_asc', 'name_desc', 'day_asc', 'day_desc', 'students_desc', 'students_asc'

    const isAdmin = currentUser?.role === 'admin';
    const [selectedClassIds, setSelectedClassIds] = useState([]);

    const fetchData = useCallback(() => {
        setIsLoading(true);
        Promise.all([
            classesAPI.getAll(),
            currentUser.role === 'admin' ? usersAPI.getAll() : Promise.resolve([]),
            currentUser.role === 'admin' ? roomsAPI.getAll() : Promise.resolve([])
        ]).then(([classesData, usersData, roomsData]) => {
            // One-off make-up classes (scheduled from the Attendance page when
            // a student is marked absent) are transient, auto-generated
            // sessions - they're managed from Attendance (scheduled/cancelled
            // there), not from this recurring-class management list, so they
            // shouldn't permanently clutter it.
            setClasses(classesData.filter(c => !c.is_one_off));
            setSelectedClassIds([]);
            setTeachers(usersData.filter(user => user.role === 'teacher' || user.role === 'admin'));
            setRooms(roomsData);
            setIsLoading(false);
        }).catch(err => {
            console.error("Failed to fetch data:", err);
            toast.error("Could not load class data.");
        }).finally(() => setIsLoading(false));
    }, [currentUser.role]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const resetAddForm = () => {
        setNewName(''); setNewStartTime(''); setNewEndTime(''); setNewDay('Monday');
        setNewTeacherId(''); setNewRoomId(''); setNewClassType('private'); setNewFormat('in_person');
        setNewCramSchoolName(''); setNewHourlyWage(''); setAddError('');
    };

    const handleOpenAddModal = () => {
        resetAddForm();
        setIsAddModalOpen(true);
        // Teachers/rooms/cram schools can change elsewhere while this page
        // stays mounted, so re-fetch whenever the Add Class modal opens.
        Promise.all([usersAPI.getAll(), roomsAPI.getAll(), studentsAPI.getCramSchools()])
            .then(([usersData, roomsData, cramSchoolsData]) => {
                setTeachers(usersData.filter(u => u.role === 'teacher' || u.role === 'admin'));
                setRooms(roomsData);
                setCramSchoolOptions(cramSchoolsData);
            }).catch(() => {});
    };

    const handleAddClass = async (e) => {
        e.preventDefault();
        setAddError('');
        if (!newTeacherId) {
            setAddError('You must select a teacher.');
            return;
        }
        const classData = {
            name: newName,
            start_time: newStartTime,
            end_time: newEndTime,
            day: newDay,
            teacher_id: newTeacherId,
            room_id: newRoomId || null,
            class_type: newClassType,
            format: newClassType === 'private' ? newFormat : 'in_person',
            cram_school_name: newClassType === 'cram_school' ? newCramSchoolName : null,
            hourly_wage: newClassType === 'cram_school' && newHourlyWage ? parseFloat(newHourlyWage) : 0
        };
        const addPromise = classesAPI.create(classData);
        toast.promise(addPromise, {
            loading: 'Adding class...',
            success: (newClass) => {
                setClasses(prev => [...prev, newClass]);
                setIsAddModalOpen(false);
                resetAddForm();
                return <b>Class added!</b>;
            },
            error: (err) => <b>{err.response?.data?.error || 'Could not add class.'}</b>
        });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const privateCount = useMemo(() => classes.filter(c => (c.class_type || 'private') === 'private').length, [classes]);
    const cramCount = useMemo(() => classes.filter(c => c.class_type === 'cram_school').length, [classes]);

    // This is the core of the filtering logic
    const filteredAndSortedClasses = useMemo(() => {
        let result = [...classes];

        // Apply category filter
        if (categoryFilter !== 'all') {
            result = result.filter(cls => (cls.class_type || 'private') === categoryFilter);
        }

        // Apply filters
        if (filters.day) {
            result = result.filter(cls => cls.day === filters.day);
        }
        if (filters.teacherId) {
            result = result.filter(cls => cls.teacher_id === parseInt(filters.teacherId));
        }

        // Apply sorting
        switch (sortBy) {
            case 'students_desc':
                result.sort((a, b) => b.student_count - a.student_count);
                break;
            case 'students_asc':
                result.sort((a, b) => a.student_count - b.student_count);
                break;
            case 'name_desc':
                result.sort((a, b) => b.name.localeCompare(a.name));
                break;
            case 'day_asc':
                result.sort((a, b) => WEEKDAY_ORDER[a.day] - WEEKDAY_ORDER[b.day]);
                break;
            case 'day_desc':
                result.sort((a, b) => WEEKDAY_ORDER[b.day] - WEEKDAY_ORDER[a.day]);
                break;
            case 'name_asc':
            default:
                result.sort((a, b) => a.name.localeCompare(b.name));
                break;
        }
        return result;
    }, [classes, categoryFilter, filters, sortBy]);

    // Toggle single class selection
    const handleToggleSelectClass = (classId) => {
        setSelectedClassIds(prev =>
            prev.includes(classId)
                ? prev.filter(id => id !== classId)
                : [...prev, classId]
        );
    };

    // Toggle select all visible classes
    const handleToggleSelectAll = () => {
        const visibleIds = filteredAndSortedClasses.map(c => c.id);
        const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedClassIds.includes(id));
        if (allSelected) {
            setSelectedClassIds(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            setSelectedClassIds(Array.from(new Set([...selectedClassIds, ...visibleIds])));
        }
    };

    // Bulk delete classes
    const handleBulkDelete = async () => {
        if (selectedClassIds.length === 0) return;
        const count = selectedClassIds.length;
        if (window.confirm(`Are you sure you want to permanently delete ${count} selected class${count > 1 ? 'es' : ''}? This will also delete all associated attendance records, enrollments, and materials.`)) {
            const deletePromise = classesAPI.bulkDelete(selectedClassIds);
            toast.promise(deletePromise, {
                loading: `Deleting ${count} class${count > 1 ? 'es' : ''}...`,
                success: () => {
                    setClasses(prevClasses => prevClasses.filter(c => !selectedClassIds.includes(c.id)));
                    setSelectedClassIds([]);
                    return <b>Successfully deleted ${count} class${count > 1 ? 'es' : ''}!</b>;
                },
                error: (err) => <b>{err.response?.data?.error || 'Failed to delete classes.'}</b>
            });
        }
    };

    // NEW: Function to open the modal and pre-fill it with student data
    const handleOpenEditModal = (cls) => {
        setEditingClass({
            ...cls,
            class_type: cls.class_type || 'private',
            format: cls.format || 'in_person',
            cram_school_name: cls.cram_school_name || '',
            hourly_wage: cls.hourly_wage || 0
        });
        setIsEditModalOpen(true);
    };

    // NEW: Function to handle form input changes in the edit modal
    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditingClass(prev => ({ ...prev, [name]: value }));
    };

    // NEW: Function to submit the update to the backend
    const handleUpdateClass = async (e) => {
        e.preventDefault();
        const updatePromise = classesAPI.update(editingClass.id, editingClass);
        toast.promise(updatePromise, {
            loading: 'Saving...',
            success: () => { // <-- FIX: No need to use the returned value
                fetchData();
                setIsEditModalOpen(false);
                return <b>Class updated!</b>;
            },
            error: (err) => <b>{err.response?.data?.error || 'Could not update.'}</b>
        });
    };

    // Pause/resume a class -- keeps it and all its attendance/payroll
    // history intact, just stops it from appearing in attendance roll-call
    // while paused (unlike Delete, which wipes that history permanently).
    const handleToggleActive = async (cls) => {
        const nextActive = !(cls.is_active !== false);
        const action = nextActive ? 'resuming' : 'pausing';
        toast.promise(
            classesAPI.setActive(cls.id, nextActive),
            {
                loading: `${nextActive ? 'Resuming' : 'Pausing'} class...`,
                success: (updated) => {
                    setClasses(prev => prev.map(c => c.id === cls.id ? { ...c, is_active: updated.is_active } : c));
                    return <b>{nextActive ? 'Class resumed!' : 'Class paused.'}</b>;
                },
                error: (err) => <b>{err.response?.data?.error || `Failed while ${action} class.`}</b>
            }
        );
    };

    // NEW: Handler for the delete button
    const handleDeleteClass = async (classId) => {
        if (window.confirm('Are you sure you want to permanently delete this class? This will also delete all associated attendance records.')) {
            // Use a toast promise for great UX
            toast.promise(
                classesAPI.delete(classId),
                {
                    loading: 'Deleting class...',
                    success: () => {
                        // On success, update the UI and return a success message
                        setClasses(prevClasses => prevClasses.filter(c => c.id !== classId));
                        setSelectedClassIds(prev => prev.filter(id => id !== classId));
                        return <b>Class deleted successfully!</b>;
                    },
                    error: (err) => <b>{err.response?.data?.error || 'Failed to delete class.'}</b>,
                }
            );
        }
    };

    if (isLoading) {
        return <div className="p-10"><Spinner /></div>;
    }

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                    <h2 className="text-2xl font-bold">
                        {isAdmin ? 'Class Management' : 'My Classes'}
                    </h2>
                    {isAdmin && selectedClassIds.length > 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {selectedClassIds.length} of {classes.length} class{selectedClassIds.length > 1 ? 'es' : ''} selected
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {isAdmin && (
                        <button
                            onClick={handleOpenAddModal}
                            className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors"
                        >
                            + Add Class
                        </button>
                    )}
                    {isAdmin && selectedClassIds.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            className="inline-flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors"
                        >
                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete Selected ({selectedClassIds.length})
                        </button>
                    )}
                </div>
            </div>

            {/* Category Tabs: All, Private Tutoring, Cram Schools */}
            <div className="flex overflow-x-auto no-scrollbar border-b border-gray-200 dark:border-gray-700 mb-4 gap-2 -mx-4 px-4 sm:mx-0 sm:px-0">
                <button
                    type="button"
                    onClick={() => setCategoryFilter('all')}
                    className={`py-2 px-3 sm:px-4 font-semibold text-xs sm:text-sm border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                        categoryFilter === 'all'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    <span>All Classes</span>
                    <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs px-2 py-0.5 rounded-full font-medium">{classes.length}</span>
                </button>
                <button
                    type="button"
                    onClick={() => setCategoryFilter('private')}
                    className={`py-2 px-3 sm:px-4 font-semibold text-xs sm:text-sm border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                        categoryFilter === 'private'
                            ? 'border-emerald-600 text-emerald-600'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    <span>🏠 Private Tutoring</span>
                    <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">{privateCount}</span>
                </button>
                <button
                    type="button"
                    onClick={() => setCategoryFilter('cram_school')}
                    className={`py-2 px-3 sm:px-4 font-semibold text-xs sm:text-sm border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                        categoryFilter === 'cram_school'
                            ? 'border-purple-600 text-purple-600'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    <span>🏫 Cram Schools</span>
                    <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium">{cramCount}</span>
                </button>
            </div>

            {currentUser.role === 'admin' && (
                <div className="p-3 sm:p-4 bg-gray-50/70 border rounded-xl mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 items-end">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">Filter by Day</label>
                        <select name="day" value={filters.day} onChange={handleFilterChange} className="w-full border rounded-lg p-2 text-sm bg-white dark:bg-gray-800">
                            <option value="">All Days</option>
                            <option>Monday</option><option>Tuesday</option><option>Wednesday</option><option>Thursday</option><option>Friday</option><option>Saturday</option><option>Sunday</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">Filter by Teacher</label>
                        <select name="teacherId" value={filters.teacherId} onChange={handleFilterChange} className="w-full border rounded-lg p-2 text-sm bg-white dark:bg-gray-800">
                            <option value="">All Teachers</option>
                            {teachers.map(t => <option key={t.id} value={t.id}>{t.username}</option>)}
                        </select>
                    </div>
                    <div>
                         <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">Sort by</label>
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full border rounded-lg p-2 text-sm bg-white dark:bg-gray-800">
                            <option value="name_asc">Class Name (A → Z)</option>
                            <option value="name_desc">Class Name (Z → A)</option>
                            <option value="day_asc">Day (Mon → Sun)</option>
                            <option value="day_desc">Day (Sun → Mon)</option>
                            <option value="students_desc">Most Students</option>
                            <option value="students_asc">Fewest Students</option>
                        </select>
                    </div>
                </div>
            )}
            {currentUser.role === 'teacher' && (
                <div className="p-3 sm:p-4 bg-gray-50/70 border rounded-xl mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">Filter by Day</label>
                        <select name="day" value={filters.day} onChange={handleFilterChange} className="w-full border rounded-lg p-2 text-sm bg-white dark:bg-gray-800">
                            <option value="">All Days</option>
                            <option>Monday</option><option>Tuesday</option><option>Wednesday</option><option>Thursday</option><option>Friday</option><option>Saturday</option><option>Sunday</option>
                        </select>
                    </div>
                    <div>
                         <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">Sort by</label>
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full border rounded-lg p-2 text-sm bg-white dark:bg-gray-800">
                            <option value="name_asc">Class Name (A → Z)</option>
                            <option value="name_desc">Class Name (Z → A)</option>
                            <option value="day_asc">Day (Mon → Sun)</option>
                            <option value="day_desc">Day (Sun → Mon)</option>
                            <option value="students_desc">Most Students</option>
                            <option value="students_asc">Fewest Students</option>
                        </select>
                    </div>
                </div>
            )}

            {/* Mobile Select All Bar */}
            {isAdmin && filteredAndSortedClasses.length > 0 && (
                <div className="flex md:hidden items-center justify-between bg-gray-50 dark:bg-gray-800/60 p-2.5 rounded-lg border mb-3 text-xs">
                    <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700 dark:text-gray-300">
                        <input
                            type="checkbox"
                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                            checked={
                                filteredAndSortedClasses.length > 0 &&
                                filteredAndSortedClasses.every(cls => selectedClassIds.includes(cls.id))
                            }
                            onChange={handleToggleSelectAll}
                        />
                        <span>Select All Displayed ({filteredAndSortedClasses.length})</span>
                    </label>
                </div>
            )}

            {/* --- MOBILE CARDS VIEW (< 768px) --- */}
            <div className="block md:hidden space-y-3">
                {filteredAndSortedClasses.length > 0 ? (
                    filteredAndSortedClasses.map(cls => (
                        <div 
                            key={`mobile-cls-${cls.id}`} 
                            className={`p-4 rounded-xl border transition-colors shadow-xs space-y-2.5 ${selectedClassIds.includes(cls.id) ? 'bg-blue-50/90 border-blue-300' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2.5">
                                    {isAdmin && (
                                        <input
                                            type="checkbox"
                                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-4 w-4 mt-1 cursor-pointer"
                                            checked={selectedClassIds.includes(cls.id)}
                                            onChange={() => handleToggleSelectClass(cls.id)}
                                        />
                                    )}
                                    <div>
                                        <Link to={`/classes/${cls.id}`} className="text-base font-bold text-blue-600 hover:underline">
                                            {cls.name}
                                        </Link>
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                            {cls.is_active === false && (
                                                <span className="inline-flex items-center text-[11px] font-bold text-amber-900 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full">
                                                    ⏸ Paused
                                                </span>
                                            )}
                                            {cls.class_type === 'cram_school' ? (
                                                <span className="inline-flex items-center text-[11px] font-bold text-purple-900 bg-purple-100 px-2 py-0.5 rounded-full">
                                                    🏫 {cls.cram_school_name || 'Cram School'}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center text-[11px] font-medium text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
                                                    🏠 Private • {cls.format === 'online' ? 'Online' : 'In-Person'}
                                                </span>
                                            )}
                                            <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-[11px] px-2 py-0.5 rounded-full font-semibold">
                                                👥 {cls.student_count} student{cls.student_count !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {currentUser.role === 'admin' && (
                                    <div className="flex items-center gap-1.5">
                                        <button onClick={() => handleOpenEditModal(cls)} className="text-xs font-semibold text-blue-600 hover:text-blue-800 p-1.5 bg-blue-50 rounded-md">Edit</button>
                                        <button
                                            onClick={() => handleToggleActive(cls)}
                                            className={`text-xs font-semibold p-1.5 rounded-md ${cls.is_active === false ? 'text-emerald-700 hover:text-emerald-900 bg-emerald-50' : 'text-amber-700 hover:text-amber-900 bg-amber-50'}`}
                                        >
                                            {cls.is_active === false ? 'Resume' : 'Pause'}
                                        </button>
                                        <button onClick={() => handleDeleteClass(cls.id)} className="text-xs font-semibold text-red-600 hover:text-red-800 p-1.5 bg-red-50 rounded-md">Delete</button>
                                    </div>
                                )}
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-800/60 p-2.5 rounded-lg border border-gray-100 dark:border-gray-700 text-xs grid grid-cols-2 gap-2">
                                <div>
                                    <span className="text-gray-500 dark:text-gray-400 block text-[10px] uppercase font-semibold">Schedule:</span>
                                    <span className="font-medium text-gray-800 dark:text-gray-200">
                                        {cls.day} {cls.start_time?.slice(0, 5)} - {cls.end_time?.slice(0, 5)}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500 dark:text-gray-400 block text-[10px] uppercase font-semibold">Teacher / Room:</span>
                                    <span className="font-medium text-gray-800 dark:text-gray-200">
                                        {cls.teacher_name || 'N/A'} {cls.room_name ? `• ${cls.room_name}` : ''}
                                    </span>
                                </div>
                                {cls.class_type === 'cram_school' && parseFloat(cls.hourly_wage) > 0 && (
                                    <div className="col-span-2 pt-1 border-t border-gray-200/60 flex justify-between items-center text-[11px]">
                                        <span className="text-purple-700 font-medium">Cram School Agreed Rate:</span>
                                        <span className="font-bold text-purple-950">NT${parseFloat(cls.hourly_wage).toLocaleString()}/hr</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <EmptyState message="No classes found matching your criteria." />
                )}
            </div>

            {/* --- DESKTOP TABLE VIEW (>= 768px) --- */}
            <div className="hidden md:block overflow-x-auto border rounded-xl shadow-xs">
                <table className="min-w-full bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/60">
                        <tr>
                            {isAdmin && (
                                <th className="w-12 py-3 px-4 text-center">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                                        checked={
                                            filteredAndSortedClasses.length > 0 &&
                                            filteredAndSortedClasses.every(cls => selectedClassIds.includes(cls.id))
                                        }
                                        onChange={handleToggleSelectAll}
                                        title="Select all visible classes"
                                    />
                                </th>
                            )}
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Class Name</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Category</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Time</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Day</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Teacher</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Students</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Room</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredAndSortedClasses.length > 0 ? (
                            filteredAndSortedClasses.map(cls => (
                                <tr 
                                    key={cls.id} 
                                    className={`hover:bg-gray-50 border-b transition-colors ${selectedClassIds.includes(cls.id) ? 'bg-blue-50' : ''}`}
                                >
                                    {isAdmin && (
                                        <td className="py-2 px-4 text-center">
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                                                checked={selectedClassIds.includes(cls.id)}
                                                onChange={() => handleToggleSelectClass(cls.id)}
                                            />
                                        </td>
                                    )}
                                    <td className="py-3 px-4">
                                        <Link to={`/classes/${cls.id}`} className="text-blue-600 hover:underline font-bold text-sm">
                                            {cls.name}
                                        </Link>
                                        {cls.is_active === false && (
                                            <span className="inline-flex items-center text-[10px] font-bold text-amber-900 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-full ml-1.5">
                                                ⏸ Paused
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-3 px-4">
                                        {cls.class_type === 'cram_school' ? (
                                            <div>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-purple-100 text-purple-800">
                                                    🏫 {cls.cram_school_name || 'Cram School'}
                                                </span>
                                                {parseFloat(cls.hourly_wage) > 0 && (
                                                    <span className="text-[11px] text-gray-500 dark:text-gray-400 block mt-0.5 font-semibold">
                                                        NT${parseFloat(cls.hourly_wage).toLocaleString()}/hr
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <div>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                                    🏠 Private
                                                </span>
                                                <span className="text-[11px] text-gray-500 dark:text-gray-400 block mt-0.5 capitalize">
                                                    {cls.format === 'online' ? 'Online (線上)' : 'In-Person (實體)'}
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-sm font-medium">{cls.start_time?.slice(0, 5)} - {cls.end_time?.slice(0, 5)}</td>
                                    <td className="py-3 px-4 text-sm">{cls.day}</td>
                                    <td className="py-3 px-4 text-sm">{cls.teacher_name || 'N/A'}</td>
                                    <td className="py-3 px-4 text-sm font-bold text-gray-900 dark:text-gray-100">{cls.student_count}</td>
                                    <td className="py-3 px-4 text-sm">{cls.room_name || 'N/A'}</td>
                                    <td className="py-3 px-4 text-sm">
                                        {currentUser.role === 'admin' && (
                                            <>
                                                <button onClick={() => handleOpenEditModal(cls)} className="text-blue-600 hover:text-blue-800 text-sm font-semibold">Edit</button>
                                                <button
                                                    onClick={() => handleToggleActive(cls)}
                                                    className={`text-sm font-semibold ml-4 ${cls.is_active === false ? 'text-emerald-600 hover:text-emerald-800' : 'text-amber-600 hover:text-amber-800'}`}
                                                >
                                                    {cls.is_active === false ? 'Resume' : 'Pause'}
                                                </button>
                                                <button onClick={() => handleDeleteClass(cls.id)} className="text-red-600 hover:text-red-800 text-sm ml-4 font-semibold">Delete</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ):(
                            <tr>
                                <td colSpan={isAdmin ? 9 : 8}>
                                    <EmptyState message="No classes found matching your criteria." />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <Modal title="Add New Class" isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
                <form onSubmit={handleAddClass}>
                    {addError && <p className="text-red-500 text-sm mb-2">{addError}</p>}

                    <div className="mb-3 bg-gray-50 dark:bg-gray-800/60 p-2.5 rounded-lg border">
                        <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase mb-1.5">Class Category</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setNewClassType('private')}
                                className={`py-2 px-3 text-xs font-semibold rounded-md border text-center transition-all ${newClassType === 'private' ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100'}`}
                            >
                                🏠 Private Tutoring (家教)
                            </button>
                            <button
                                type="button"
                                onClick={() => setNewClassType('cram_school')}
                                className={`py-2 px-3 text-xs font-semibold rounded-md border text-center transition-all ${newClassType === 'cram_school' ? 'bg-purple-600 text-white border-purple-600 shadow-sm' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100'}`}
                            >
                                🏫 Cram School (補習班)
                            </button>
                        </div>
                    </div>

                    {newClassType === 'private' ? (
                        <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Format</label>
                            <select value={newFormat} onChange={e => setNewFormat(e.target.value)} className="w-full border rounded p-2 text-sm">
                                <option value="in_person">In-Person (實體 1v1: 650, 1v2: 350, 1v3+: 300 NTD)</option>
                                <option value="online">Online (線上 600 NTD)</option>
                            </select>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 bg-purple-50 p-2.5 rounded-lg border border-purple-200">
                            <div>
                                <label className="block text-xs font-medium text-purple-900 mb-1">Cram School Name</label>
                                <input
                                    type="text"
                                    list="add-class-cram-school-options"
                                    value={newCramSchoolName}
                                    onChange={e => setNewCramSchoolName(e.target.value)}
                                    placeholder="e.g. Joy English, Kid Castle"
                                    className="w-full border rounded p-2 text-sm"
                                    required={newClassType === 'cram_school'}
                                />
                                <datalist id="add-class-cram-school-options">
                                    {cramSchoolOptions.map(name => <option key={name} value={name} />)}
                                </datalist>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-purple-900 mb-1">Hourly Wage NTD</label>
                                <input type="number" value={newHourlyWage} onChange={e => setNewHourlyWage(e.target.value)} placeholder="e.g. 800" className="w-full border rounded p-2 text-sm" />
                            </div>
                        </div>
                    )}

                    <div className="mb-2"><label className="text-xs font-medium text-gray-700 dark:text-gray-300">Class Name</label><input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Minecraft Monday" className="w-full border rounded p-2 text-sm" required /></div>

                    <div className="grid grid-cols-2 gap-2 mb-2">
                        <div><label className="text-xs font-medium text-gray-700 dark:text-gray-300">Start Time</label><input type="time" value={newStartTime} onChange={e => setNewStartTime(e.target.value)} className="w-full border rounded p-2 text-sm" required /></div>
                        <div><label className="text-xs font-medium text-gray-700 dark:text-gray-300">End Time</label><input type="time" value={newEndTime} onChange={e => setNewEndTime(e.target.value)} className="w-full border rounded p-2 text-sm" required /></div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-2">
                        <div><label className="text-xs font-medium text-gray-700 dark:text-gray-300">Day of Week</label><select value={newDay} onChange={e => setNewDay(e.target.value)} className="w-full border rounded p-2 text-sm">
                            <option>Monday</option><option>Tuesday</option><option>Wednesday</option><option>Thursday</option><option>Friday</option><option>Saturday</option><option>Sunday</option>
                        </select></div>
                        <div><label className="text-xs font-medium text-gray-700 dark:text-gray-300">Teacher</label><select value={newTeacherId} onChange={e => setNewTeacherId(e.target.value)} className="w-full border rounded p-2 text-sm" required>
                            <option value="">-- Select Teacher --</option>
                            {teachers.map(t => <option key={t.id} value={t.id}>{t.username}</option>)}
                        </select></div>
                    </div>

                    <div className="mb-4">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Room / Venue (Optional)</label>
                        <select value={newRoomId} onChange={e => setNewRoomId(e.target.value)} className="w-full border rounded p-2 text-sm">
                            <option value="">-- Select a Room --</option>
                            {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </div>

                    <div className="flex justify-end space-x-2 pt-2 border-t">
                        <button type="button" onClick={() => setIsAddModalOpen(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-sm">Cancel</button>
                        <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium">Add Class</button>
                    </div>
                </form>
            </Modal>

             {editingClass && (
                <Modal title="Edit Class" isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
                        <form onSubmit={handleUpdateClass}>
                            {/* Class Category Selector */}
                            <div className="mb-3 bg-gray-50 dark:bg-gray-800/60 p-2.5 rounded-lg border">
                                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase mb-1.5">Class Category</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setEditingClass(prev => ({ ...prev, class_type: 'private' }))}
                                        className={`py-2 px-3 text-xs font-semibold rounded-md border text-center transition-all ${
                                            editingClass.class_type !== 'cram_school'
                                                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🏠 Private Tutoring (家教)
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditingClass(prev => ({ ...prev, class_type: 'cram_school' }))}
                                        className={`py-2 px-3 text-xs font-semibold rounded-md border text-center transition-all ${
                                            editingClass.class_type === 'cram_school'
                                                ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🏫 Cram School (補習班)
                                    </button>
                                </div>
                            </div>

                            {editingClass.class_type === 'cram_school' ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 bg-purple-50 p-2.5 rounded-lg border border-purple-200">
                                    <div>
                                        <label className="block text-xs font-medium text-purple-900 mb-1">Cram School Name</label>
                                        <input
                                            type="text"
                                            name="cram_school_name"
                                            value={editingClass.cram_school_name || ''}
                                            onChange={handleEditFormChange}
                                            placeholder="e.g. Joy English, Kid Castle"
                                            className="w-full border rounded p-2 text-sm"
                                            required={editingClass.class_type === 'cram_school'}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-purple-900 mb-1">Hourly Wage NTD</label>
                                        <input
                                            type="number"
                                            name="hourly_wage"
                                            value={editingClass.hourly_wage || ''}
                                            onChange={handleEditFormChange}
                                            placeholder="e.g. 800"
                                            className="w-full border rounded p-2 text-sm"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-3">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Format</label>
                                    <select
                                        name="format"
                                        value={editingClass.format || 'in_person'}
                                        onChange={handleEditFormChange}
                                        className="w-full border rounded p-2 text-sm"
                                    >
                                        <option value="in_person">In-Person (實體 1v1: 650, 1v2: 350, 1v3+: 300 NTD)</option>
                                        <option value="online">Online (線上 600 NTD)</option>
                                    </select>
                                </div>
                            )}

                            <div className="mb-2"><label className="text-xs font-medium text-gray-700 dark:text-gray-300">Class Name</label><input type="text" name="name" value={editingClass.name} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm" required /></div>
                            
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <div><label className="text-xs font-medium text-gray-700 dark:text-gray-300">Start Time</label><input type="time" name="start_time" value={editingClass.start_time} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm" required /></div>
                                <div><label className="text-xs font-medium text-gray-700 dark:text-gray-300">End Time</label><input type="time" name="end_time" value={editingClass.end_time} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm" required /></div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <div><label className="text-xs font-medium text-gray-700 dark:text-gray-300">Day of Week</label><select name="day" value={editingClass.day} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm"><option>Monday</option><option>Tuesday</option><option>Wednesday</option><option>Thursday</option><option>Friday</option><option>Saturday</option><option>Sunday</option></select></div>
                                <div><label className="text-xs font-medium text-gray-700 dark:text-gray-300">Teacher</label><select name="teacher_id" value={editingClass.teacher_id} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm" required>
                                    <option value="">-- Select Teacher --</option>
                                    {teachers.map(t => <option key={t.id} value={t.id}>{t.username}</option>)}
                                </select></div>
                            </div>

                            <div className="mb-4">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Room / Venue (Optional)</label>
                                <select name="room_id" value={editingClass.room_id || ''} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm">
                                    <option value="">-- Select a Room --</option>
                                    {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                </select>
                            </div>
                            <div className="flex justify-end space-x-2 pt-2 border-t">
                                <button type="button" onClick={() => setIsEditModalOpen(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-sm">Cancel</button>
                                <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium">Save Changes</button>
                            </div>
                        </form>
                </Modal>
            )}
        </div>
    );
}